import { Modal, PrimaryButton, Text, GhostButton } from "@certn/cascadia";
import {
  Container,
  Dialog,
  Layout,
  SecondaryTopNav,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { t } from "i18next";
import { PropsWithChildren, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DCOReviewContext } from "./DCOReviewContext";
import { ExitDCOReviewFormModal } from "./ExitDCOReviewFormModal";

interface Props extends PropsWithChildren {
  onComplete?: () => void;
  nextDisabled: boolean;
}

export function DCOReviewPageLayout(props: Props) {
  const { onComplete, children, nextDisabled } = props;
  const navigate = useNavigate();
  const { goToPrevStep, currentStep } = useContext(DCOReviewContext);
  const [isExitDCOReviewFormModalVisible, setIsExitDCOReviewFormModalVisible] =
    useState(false);
  const navigateBack = () => navigate("/");

  const handleExitForm = () => {
    if (atLastStep) {
      navigate("/");
    } else {
      setIsExitDCOReviewFormModalVisible(true);
    }
  };

  const steps = [
    t("Review Results Containing Disclosable Court Outcomes"),
    t("Review your Check Result"),
    t("Would you like to dispute your results?"),
    t("Dispute your Results"),
    t("Success!"),
  ];

  const atLastStep = currentStep >= steps.length - 1;

  return (
    <Layout
      layout="narrow"
      header={
        <SecondaryTopNav
          testID="close-button"
          leftIcon="close"
          onleftIconOnPress={handleExitForm}
        >
          {currentStep > 0 && !atLastStep && (
            <GhostButton
              onPress={goToPrevStep}
              size="medium"
              testID="previous-step-button"
            >
              {t("Back")}
            </GhostButton>
          )}

          <PrimaryButton
            onPress={onComplete}
            isDisabled={nextDisabled}
            testID="next-step-complete-dco-review-button"
          >
            {!atLastStep
              ? currentStep === 3
                ? t("Submit Dispute")
                : t("Next step")
              : t("Return to dashboard")}
          </PrimaryButton>
        </SecondaryTopNav>
      }
    >
      <Container
        testID={
          steps[currentStep].replaceAll(" ", "-").toLowerCase() + "-container"
        }
      >
        <Modal testID="exit-dco-modal" isOpen={isExitDCOReviewFormModalVisible}>
          <Dialog>
            <ExitDCOReviewFormModal
              onBackToHub={navigateBack}
              onClose={() => {
                setIsExitDCOReviewFormModalVisible(false);
              }}
            />
          </Dialog>
        </Modal>
        <div className={styles.container}>
          <div>
            <Text style="card/title-large">{steps[currentStep]}</Text>
          </div>
          {children}
        </div>
      </Container>
    </Layout>
  );
}

const styles = {
  container: css({
    paddingLeft: "var(--space-8)",
    paddingRight: "var(--space-8)",
    paddingTop: "var(--space-7)",
    paddingBottom: "var(--space-7)",
    gap: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
  }),
};
